import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { UserIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { Link } from '@tanstack/react-router';
import { useModal } from 'connectkit';
import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { useAccount } from 'wagmi';
import { DEFAULT_SUBACCOUNT, TESTNET_FORM_URL } from '../../constants';
import { PORTFOLIO_PAGE, SUBACCOUNT_PICKER } from '../../constants/featureFlags';
import { useDepositsEnabled } from '../../hooks/useDepositsEnabled';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useModalState } from '../../hooks/useModalState';
import { useNetworkIsValid } from '../../hooks/useNetworkIsValid';
import { usePendingSubaccount, useSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { defaultSelectedTicker, isDev } from '../../lib/config';
import { decodeSubaccountName } from '../../lib/subaccountHelpers';
import { FileRouteTypes } from '../../routeTree.gen';
import { Card } from '../Card';
import { Button } from '../catalyst-ui-kit/button';
import { Navbar, NavbarItem } from '../catalyst-ui-kit/navbar';
import { ConnectButton } from '../ConnectButton/ConnectButton';
import { OnboardedButton } from '../OnboardedButton/OnboardedButton';
import { useSettingsState } from '../SettingsModal/SettingsState';
import { Tooltip } from '../Tooltip/Tooltip';
import { HamburgerMenu } from './HamburgerMenu';

export type NavLink = {
  to: FileRouteTypes['fullPaths'];
  params?: { asset: string };
  label: string;
  disabled?: boolean;
  childRoutes?: NavLink[];
};

export const Header = () => {
  const depositsEnabled = useDepositsEnabled();
  const { value: subaccountPickerEnabled } = useFeatureFlag(SUBACCOUNT_PICKER);
  const { isConnected, chain } = useAccount();
  const { openSwitchNetworks } = useModal();
  const { value: selectedSubaccount } = useSelectedSubaccount();
  const { value: pendingSubaccount } = usePendingSubaccount();
  const subaccountName = selectedSubaccount ? selectedSubaccount.name : pendingSubaccount;
  const setSettingsModal = useModalState(({ setSettingsModal }) => setSettingsModal);
  const setDepositModal = useModalState(({ setDepositModal }) => setDepositModal);
  const pushPanel = useSettingsState(({ pushPanel }) => pushPanel);
  const { value: portfolioPageEnabled } = useFeatureFlag(PORTFOLIO_PAGE);
  const { data: networkIsValid, isPending: validatingNetwork } = useNetworkIsValid();
  const isMd = useTailwindBreakpoint('md');

  const handleClickSubaccount = () => {
    setSettingsModal('one-click');
    pushPanel('dev-settings');
  };

  const navLinks = useMemo(() => {
    const baseNavLinks: NavLink[] = [
      {
        to: '/trade/$asset',
        params: { asset: defaultSelectedTicker },
        label: 'Trade',
      },
      {
        to: '/portfolio',
        label: 'Portfolio',
        disabled: !portfolioPageEnabled,
        childRoutes: [
          {
            to: '/portfolio/overview',
            label: 'Overview',
          },
          {
            to: '/portfolio/positions',
            label: 'Positions',
          },
          {
            to: '/portfolio/orders',
            label: 'Open Orders',
          },
          {
            to: '/portfolio/trades',
            label: 'Trade History',
          },
          {
            to: '/portfolio/funding',
            label: 'Funding History',
            disabled: true,
          },
          {
            to: '/portfolio/sessions',
            label: 'Trading Sessions',
          },
        ],
      },
      {
        to: '/points',
        label: 'Points',
        disabled: true,
      },
    ];

    const devLinks: NavLink[] = [
      {
        to: '/mintable',
        label: 'Mintable',
      },
    ];

    return isDev ? [...baseNavLinks, ...devLinks] : baseNavLinks;
  }, [portfolioPageEnabled]);

  return (
    <Card className="flex justify-between p-2 md:px-3">
      <div className="flex items-center gap-2 md:gap-4">
        {!isMd && <HamburgerMenu links={navLinks} />}
        <Link to="/">
          <img
            src={!isMd ? '/logo-small.svg' : '/logo.svg'}
            alt="logo"
            className="h-6 w-auto md:h-5"
          />
        </Link>
        {isMd && (
          <Navbar className="flex items-center gap-2">
            {navLinks.map(({ to, params, label, disabled }) => (
              <NavbarItem
                key={to}
                className={disabled ? 'cursor-not-allowed opacity-50 hover:dark:bg-none' : ''}
                to={to}
                disabled={disabled}
                params={params}
              >
                <Tooltip content={disabled ? 'Coming Soon' : undefined}>{label}</Tooltip>
              </NavbarItem>
            ))}
          </Navbar>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isDev && chain && (
          <Button
            plain
            size="sm"
            onClick={openSwitchNetworks}
            className={twJoin(
              'hidden gap-1.5 lg:flex',
              !validatingNetwork && !networkIsValid
                ? 'border border-contentRed !py-px !text-contentRed'
                : ''
            )}
          >
            <span
              className={twJoin(
                'size-2 rounded-full',
                match({ validatingNetwork, networkIsValid })
                  .with({ validatingNetwork: true }, () => 'animate-pulse bg-contentYellow')
                  .with({ networkIsValid: true }, () => 'bg-contentGreen')
                  .otherwise(() => 'bg-contentRed')
              )}
            />
            {chain?.name}
          </Button>
        )}
        {(isDev || subaccountPickerEnabled) &&
          subaccountName &&
          subaccountName !== DEFAULT_SUBACCOUNT && (
            <Button
              plain
              size="sm"
              onClick={handleClickSubaccount}
              className="hidden max-w-32 gap-1.5 lg:flex"
            >
              {subaccountName === pendingSubaccount ? (
                <UserPlusIcon className="size-4 fill-contentBlue" />
              ) : (
                <UserIcon className="size-4" />
              )}
              <span className="truncate">{decodeSubaccountName(subaccountName)}</span>
            </Button>
          )}
        {isConnected && isMd && (
          <OnboardedButton overrideLabel="Deposit" dependsOnStep="terms">
            <Button
              className={depositsEnabled ? '' : 'hidden'}
              size="sm"
              color="blue"
              onClick={() => {
                if (!depositsEnabled) {
                  location.href = TESTNET_FORM_URL;
                  return;
                }
                return setDepositModal({ openOrClosed: 'open', context: 'Onboard' });
              }}
            >
              Deposit
            </Button>
          </OnboardedButton>
        )}
        <ConnectButton size="sm" />
        {isConnected && (
          <Button
            size="sm"
            className="h-full"
            onClick={() => {
              setSettingsModal('one-click');
            }}
          >
            <Cog6ToothIcon className="stroke-contentSecondary" />
          </Button>
        )}
      </div>
    </Card>
  );
};
